// 현장 사진의 크기·형식·저장 키 — 형식은 `file.type` 이 아니라 매직바이트로 가른다(SVG·GIF·HEIC 거절).

import type { PhotoMimeType } from "./types";

// `0025` 의 project_photo.size_bytes check 와 같은 값.
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

export const MAX_PHOTO_CAPTION = 200;

const startsWith = (bytes: Uint8Array, sig: readonly number[], at = 0): boolean =>
  bytes.length >= at + sig.length && sig.every((b, i) => bytes[at + i] === b);

const JPEG = [0xff, 0xd8, 0xff] as const;
const PNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;
const RIFF = [0x52, 0x49, 0x46, 0x46] as const;
const WEBP = [0x57, 0x45, 0x42, 0x50] as const;

export const sniffImageMime = (bytes: Uint8Array): PhotoMimeType | null => {
  if (startsWith(bytes, JPEG)) return "image/jpeg";
  if (startsWith(bytes, PNG)) return "image/png";
  if (startsWith(bytes, RIFF) && startsWith(bytes, WEBP, 8)) return "image/webp";
  return null;
};

const EXT: Record<PhotoMimeType, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

export const extOfMime = (mime: PhotoMimeType): string => EXT[mime];

// R2 키로 그대로 쓴다 — 바이트는 행의 storage_path 로만 찾는다.
export const photoStoragePath = (
  orgId: string,
  projectId: string,
  photoId: string,
  mime: PhotoMimeType
): string => `org/${orgId}/project/${projectId}/photo/${photoId}.${extOfMime(mime)}`;

// 대화 사진 자리 — `0120` project_message_change_guard 가 「접두 + 글 id + mime 확장자」 그대로만 받는다.
export const messagePhotoPrefix = (orgId: string, projectId: string): string => `org/${orgId}/project/${projectId}/message/`;

export const messagePhotoStoragePath = (
  orgId: string,
  projectId: string,
  messageId: string,
  mime: PhotoMimeType
): string => `${messagePhotoPrefix(orgId, projectId)}${messageId}.${extOfMime(mime)}`;

// 그 글 자기 자리의 사진인지 — 경로가 messagePhotoStoragePath 와 정확히 같아야 한다(`0120` 트리거와 같은 비교).
export const isMessagePhotoPathOf = (
  path: string,
  orgId: string,
  projectId: string,
  messageId: string,
  mime: PhotoMimeType
): boolean => path === messagePhotoStoragePath(orgId, projectId, messageId, mime);

export const PHOTO_BLOCK = {
  empty: "빈 파일은 올릴 수 없습니다",
  tooLarge: "사진은 한 장에 5MB 까지 올릴 수 있습니다",
  badType: "JPEG·PNG·WEBP 사진만 올릴 수 있습니다",
  damaged: "사진 파일이 손상되어 올릴 수 없습니다",
  captionTooLong: `설명은 ${MAX_PHOTO_CAPTION}자까지 쓸 수 있습니다`,
} as const;

// 크기만 — 브라우저가 보내기 전에, 서버가 바이트를 읽기 전에 같은 자로 본다.
export const photoSizeBlockReason = (size: number): string | null => {
  if (size <= 0) return PHOTO_BLOCK.empty;
  if (size > MAX_PHOTO_BYTES) return PHOTO_BLOCK.tooLarge;
  return null;
};

export type PhotoInspection = { ok: true; mime: PhotoMimeType } | { ok: false; reason: string };

// 올리기 전 검사 — 크기 먼저, 그다음 형식.
export const inspectPhoto = (bytes: Uint8Array): PhotoInspection => {
  const sizeReason = photoSizeBlockReason(bytes.length);
  if (sizeReason) return { ok: false, reason: sizeReason };
  const mime = sniffImageMime(bytes);
  return mime === null ? { ok: false, reason: PHOTO_BLOCK.badType } : { ok: true, mime };
};
